import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { Button } from './Button';
import { Icon } from './Icon';

const MenuContext = createContext(null);

/** Room the panel needs below its button before it opens upwards instead. */
const PANEL_ROOM = 240;

/**
 * "More": the overflow of a card's footer. A card shows its main action and
 * the two most used as labelled buttons; anything past that goes in here.
 *
 *   <ActionMenu label="More actions for Anirban">
 *     <ActionMenuItem icon="send" onSelect={...}>Send notice</ActionMenuItem>
 *     <ActionMenuItem icon="eye" to="/admin/...">View record</ActionMenuItem>
 *   </ActionMenu>
 *
 * Keyboard: Enter, Space or Arrow Down on the button opens it with the first
 * item focused; Arrow Up / Down, Home and End move between items; Escape or
 * Tab closes it, and Escape puts focus back on the button. A click anywhere
 * else closes it too.
 *
 * The panel is drawn on `document.body` at fixed coordinates, so a list card
 * with `overflow: hidden` cannot cut it off, and it stays mounted while closed
 * (hidden): an item that opens its own dialog -- Suspend, Send notice -- keeps
 * that dialog alive after the menu has closed. When none of the children
 * renders an item (every one of them gated off for this row), the button
 * hides itself rather than open an empty menu.
 */
export function ActionMenu({ label = 'More actions', children, className }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [hasItems, setHasItems] = useState(true);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const menuId = useId();

  const items = () => [...(panelRef.current?.querySelectorAll('[role="menuitem"]') ?? [])];

  // Children are gated components that may render nothing for this row.
  useLayoutEffect(() => {
    const any = items().length > 0;
    if (any !== hasItems) setHasItems(any);
  });

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom >= PANEL_ROOM || rect.top < PANEL_ROOM;
    setPosition({
      top: below ? rect.bottom + 4 : undefined,
      bottom: below ? undefined : window.innerHeight - rect.top + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  const close = useCallback((refocus = true) => {
    // Focus goes back first, so a dialog an item opens remembers the button
    // (not a now-hidden item) as the place to return focus to.
    if (refocus) triggerRef.current?.focus();
    setOpen(false);
  }, []);

  function show(focus = 'first') {
    place();
    setOpen(true);
    requestAnimationFrame(() => {
      const list = items();
      (focus === 'last' ? list[list.length - 1] : list[0])?.focus();
    });
  }

  // Outside clicks, scrolling and resizing close it: a panel left behind by
  // a scroll would point at nothing.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      close(false);
    }
    function onScroll(event) {
      if (panelRef.current?.contains(event.target)) return;
      close(false);
    }
    const onResize = () => close(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  function onTriggerKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      show('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      show('last');
    }
  }

  function onPanelKeyDown(event) {
    const list = items();
    const index = list.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      close(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      list[(index + 1) % list.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      list[(index - 1 + list.length) % list.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      list[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      list[list.length - 1]?.focus();
    }
  }

  return (
    <MenuContext.Provider value={{ close }}>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        hidden={!hasItems}
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        title={label}
        onClick={() => (open ? close() : show())}
        onKeyDown={onTriggerKeyDown}
      >
        <Icon name="more" size={14} />
        More
      </Button>
      {createPortal(
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={label}
          hidden={!open}
          onKeyDown={onPanelKeyDown}
          style={
            position
              ? {
                  position: 'fixed',
                  top: position.top,
                  bottom: position.bottom,
                  right: position.right,
                }
              : undefined
          }
          className="border-line bg-surface rounded-input z-40 max-w-[calc(100vw-16px)] min-w-44 border p-1 text-left shadow-lg"
        >
          {children}
        </div>,
        document.body,
      )}
    </MenuContext.Provider>
  );
}

const ITEM =
  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm font-medium transition ' +
  'focus:outline-none disabled:cursor-not-allowed disabled:opacity-60';

const ITEM_TONES = {
  default: 'text-ink hover:bg-brand-50 hover:text-brand-700 focus:bg-brand-50 focus:text-brand-700',
  danger: 'text-danger-700 hover:bg-danger-50 focus:bg-danger-50',
};

/**
 * One entry in an `ActionMenu`: a button (`onSelect`) or a link (`to`).
 * Selecting it closes the menu first, then runs `onSelect`.
 *
 * `reason` says why a disabled entry is unavailable. It shows under the
 * label as small muted text -- a tooltip alone is invisible on a phone and
 * easy to miss with a mouse -- and is also the entry's accessible description.
 */
export function ActionMenuItem({
  icon,
  to,
  onSelect,
  tone = 'default',
  disabled = false,
  reason = null,
  title,
  children,
}) {
  const menu = useContext(MenuContext);
  const reasonId = useId();
  const showReason = disabled && Boolean(reason);
  const className = cn(
    ITEM,
    ITEM_TONES[tone] ?? ITEM_TONES.default,
    showReason && 'items-start disabled:opacity-100',
  );
  const content = (
    <>
      {icon && (
        <Icon name={icon} size={14} className={cn('shrink-0', showReason && 'mt-0.5 opacity-60')} />
      )}
      <span className="min-w-0">
        <span className={cn('block', showReason && 'opacity-60')}>{children}</span>
        {showReason && (
          <span id={reasonId} className="text-ink-muted mt-0.5 block text-xs font-normal">
            {reason}
          </span>
        )}
      </span>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        role="menuitem"
        tabIndex={-1}
        title={title}
        className={className}
        onClick={() => menu?.close(false)}
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      title={title ?? (showReason ? reason : undefined)}
      aria-describedby={showReason ? reasonId : undefined}
      disabled={disabled}
      className={className}
      onClick={() => {
        menu?.close();
        onSelect?.();
      }}
    >
      {content}
    </button>
  );
}
